import { InfoCircleOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import React from 'react';

const { Text } = Typography;

/**
 * Example transformation showing before → after for Charter prefix
 */
interface ExampleProps {
  before: string;
  after: string;
  isSkipped?: boolean;
}

const ExampleTransformation: React.FC<ExampleProps> = ({ before, after, isSkipped }) => (
  <div style={styles.exampleRow}>
    <Text code style={styles.exampleCode}>{before}</Text>
    <ArrowRightOutlined style={styles.arrow} />
    <Text code style={{ ...styles.exampleCode, ...(isSkipped ? styles.skippedCode : styles.resultCode) }}>
      {after}
    </Text>
    {isSkipped && <Text type="secondary" style={styles.skipNote}>(no change)</Text>}
  </div>
);

/**
 * Informational component displayed on address create/edit forms for Charter business.
 * Explains the automatic "Charter " prefix behavior in a clear, visually appealing way.
 */
export const CharterAddressPrefixInfo: React.FC = () => {
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <InfoCircleOutlined style={styles.icon} />
        <Text strong style={styles.title}>Charter Address Naming</Text>
      </div>

      <div style={styles.content}>
        <Text style={styles.description}>
          All Charter addresses are automatically prefixed with <Text code style={styles.prefix}>"Charter "</Text> in the Name field.
          This ensures packing slips clearly identify Charter shipping locations.
        </Text>

        <div style={styles.examplesContainer}>
          <Text type="secondary" style={styles.examplesLabel}>Examples:</Text>
          <div style={styles.examplesList}>
            <ExampleTransformation before="Main Warehouse" after="Charter Main Warehouse" />
            <ExampleTransformation before="Atlanta Hub" after="Charter Atlanta Hub" />
            <ExampleTransformation before="Charter HQ" after="Charter HQ" isSkipped />
          </div>
        </div>

        <Text type="secondary" style={styles.footnote}>
          Names already containing "Charter" will not be modified.
        </Text>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    background: 'linear-gradient(135deg, #f0f7ff 0%, #e6f2ff 100%)',
    border: '1px solid #91caff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.08)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 10,
    borderBottom: '1px solid rgba(24, 144, 255, 0.2)'
  },
  icon: {
    fontSize: 20,
    color: '#1890ff',
    marginRight: 10
  },
  title: {
    fontSize: 15,
    color: '#0958d9',
    letterSpacing: 0.3
  },
  content: {
    paddingLeft: 30
  },
  description: {
    fontSize: 13,
    color: '#262626',
    lineHeight: 1.6,
    display: 'block',
    marginBottom: 14
  },
  prefix: {
    backgroundColor: '#e6f4ff',
    color: '#0958d9',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: 4
  },
  examplesContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: 6,
    padding: 12,
    marginBottom: 10
  },
  examplesLabel: {
    fontSize: 12,
    fontWeight: 500,
    display: 'block',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  examplesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  exampleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap'
  },
  exampleCode: {
    fontSize: 12,
    padding: '3px 8px',
    borderRadius: 4,
    backgroundColor: '#fafafa',
    border: '1px solid #d9d9d9'
  },
  resultCode: {
    backgroundColor: '#f6ffed',
    borderColor: '#b7eb8f',
    color: '#389e0d'
  },
  skippedCode: {
    backgroundColor: '#fff7e6',
    borderColor: '#ffd591',
    color: '#d46b08'
  },
  arrow: {
    color: '#8c8c8c',
    fontSize: 12
  },
  skipNote: {
    fontSize: 11,
    fontStyle: 'italic'
  },
  footnote: {
    fontSize: 12,
    fontStyle: 'italic',
    display: 'block'
  }
};

export default CharterAddressPrefixInfo;
